/* eslint-disable consistent-return */
import Grid from '@config/Grid/Grid'
import Tile from '@/shared/components/Tile/Tile'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from '@/features/feedback/FeedbackPage.module.scss'
import FeedbackSummary from '@/features/feedback/components/FeedbackSummary/FeedbackSummary'
import FeedbackTile from '@/features/feedback/components/FeedbackTile/FeedbackTile'
import IconButton from '@/shared/components/IconButton/IconButton'
import DatePicker from '@/shared/components/DatePicker/DatePicker'
import Alert from '@/shared/components/Alert/Alert'
import CheckBox from '@/shared/components/CheckBox/CheckBox'
import {
  ReviewDestination,
  useReviewInviteQuery,
  useReviewInviteStore
} from '@/features/feedback'
import { useGoogleLocationQuery } from '@/features/google-location'

/**
 * Slides the whole tile to the bottom, which hides the header and the summary
 * and leaves the review list filling it, or back to the top.
 *
 * Module level on purpose: both the expand button and the listeners below call
 * it, and a function defined in the component body would have to be a dependency
 * of the effect, re-subscribing every render.
 */
function scrollPageTo(page: HTMLDivElement | null, toBottom: boolean) {
  if (!page) return
  page.scrollTo({ top: toBottom ? page.scrollHeight : 0, behavior: 'smooth' })
}

function FeedbackPage() {
  const { t } = useTranslation()
  const pageContainerRef = useRef<HTMLDivElement>(null)
  const tilesContainerRef = useRef<HTMLDivElement>(null)
  const [isExpanded, setIsExpanded] = useState(false)

  const isActive = useReviewInviteStore(s => s.isActive)
  const setIsActive = useReviewInviteStore(s => s.setIsActive)

  useReviewInviteQuery()

  /*
    Said only once the connection has been read: a warning that flashes up
    and goes while it loads reads as a fault that fixed itself.
  */
  const googleLocation = useGoogleLocationQuery()
  const isMissingPlace =
    isActive &&
    googleLocation.isSuccess &&
    googleLocation.data?.status !== 'active'

  const handleExpandClick = () => {
    scrollPageTo(pageContainerRef.current, !isExpanded)
    setIsExpanded(!isExpanded)
  }

  /*
    Scrolling the review list does what the expand button does.

    The listeners are on the list, not on the tile around it, because the list
    is what the pointer is over: it has its own scrollbar, so the wheel never
    reaches the tile and the tile's own scroll handler used to sit there waiting
    for an event that only arrived after the list had been scrolled to its very
    end. Now the first notch down slides the header away, and coming back to the
    top of the list brings it back. The button's icon follows either way, since
    both paths set the same flag.
  */
  useEffect(() => {
    const list = tilesContainerRef.current
    if (!list) return

    let last = list.scrollTop

    const expand = () => {
      scrollPageTo(pageContainerRef.current, true)
      setIsExpanded(true)
    }

    const collapse = () => {
      scrollPageTo(pageContainerRef.current, false)
      setIsExpanded(false)
    }

    const handleWheel = (event: WheelEvent) => {
      if (!isExpanded && event.deltaY > 0) {
        expand()
        return
      }
      /*
        A list already at its top has nothing left to scroll, so it fires no
        scroll event at all. The wheel is the only thing that can ask for the
        header back from there.
      */
      if (isExpanded && event.deltaY < 0 && list.scrollTop === 0) collapse()
    }

    // Touch produces no wheel events; a swipe on a tablet has only this.
    const handleScroll = () => {
      const top = list.scrollTop
      if (!isExpanded && top > last) expand()
      else if (isExpanded && top === 0 && top < last) collapse()
      last = top
    }

    list.addEventListener('wheel', handleWheel, { passive: true })
    list.addEventListener('scroll', handleScroll)
    return () => {
      list.removeEventListener('wheel', handleWheel)
      list.removeEventListener('scroll', handleScroll)
    }
  }, [isExpanded])

  return (
    <Grid>
      {/*
        Narrow left column for the settings that decide where an opinion goes,
        wide right one for the opinions that came back. The same split the
        loyalty tab uses between what is set and what it produced.
      */}
      <div className={`${styles.feedbackSettingsColumn} start-2 col-3`}>
        <Tile color="black">
          <div className={styles.feedbackSettingsContainer}>
            <p className={`${styles.feedbackHeader} title-bold-medium`}>
              {t('FEEDBACK.INVITE.TITLE')}
            </p>
            {/*
              Two sentences saying what the tile is for. Everything past that is
              said by the field labels and the one-liners under them.
            */}
            <p className={`${styles.feedbackSettingsLead} body-medium`}>
              {t('FEEDBACK.INVITE.DESCRIPTION')}
            </p>

            {isMissingPlace && (
              <Alert
                severity="warning"
                message={
                  // Connected, but to a place Google has since dropped: the
                  // way out is picking it again, as Settings says too.
                  googleLocation.data?.status === 'needs_reselect'
                    ? t('FEEDBACK.INVITE.RESELECT_PLACE')
                    : t('FEEDBACK.INVITE.MISSING_PLACE')
                }
              />
            )}

            {/*
              Saved as it is ticked: it is the one setting left here, and a
              save button for a single box only adds a way to lose the change.
            */}
            <CheckBox
              label={t('FEEDBACK.INVITE.ACTIVE')}
              checked={isActive}
              value={isActive}
              onChange={setIsActive}
            />

            <div className={styles.inviteAnchor} id="tour-feedback-invite">
              <ReviewDestination disabled={!isActive} />
            </div>
          </div>
        </Tile>
      </div>

      <div className={`${styles.feedbackPageWrapper} start-5 col-8`}>
        <Tile color="black">
          <div className={styles.feedbackPageContainer} ref={pageContainerRef}>
            <div className={styles.feedbackHeaderContainer}>
              <div
                id="tour-feedback-content"
                className={styles.feedbackHeaderLeftWrapper}
              >
                <p className={`${styles.feedbackHeader} title-bold-medium`}>
                  {t('FEEDBACK.TITLE')}
                </p>
                <div className={styles.feedbackDescriptionContainer}>
                  <p className={`${styles.feedbackDescription} title-large`}>
                    87% positive feedback.
                  </p>
                  <p
                    className={`${styles.feedbackDescriptionSmall} title-small`}
                  >
                    Most customers rated their overall experience as great.
                  </p>
                  <p
                    className={`${styles.feedbackDescriptionSmall} title-small`}
                  >
                    Compared to last week:{' '}
                    <mark className="mark-white">+5%</mark>
                  </p>
                </div>
              </div>
              <FeedbackSummary />
            </div>
            <div className={styles.feedbackTilesWrapper}>
              <div className={styles.feedbackTilesHeader}>
                <div className={styles.feedbackTilesHeaderLeftSideItems}>
                  <DatePicker label={t('COMMON.DATE_RANGE')} />
                  {/* <TooltipButton icon="las la-filter icon-big">
                    test
                  </TooltipButton>
                  <TooltipButton icon="las la-user-circle icon-big">
                    test
                  </TooltipButton> */}
                </div>
                <IconButton
                  icon={
                    isExpanded
                      ? 'las la-compress icon-big'
                      : 'las la-expand icon-big'
                  }
                  onClick={handleExpandClick}
                  color="primary"
                />
              </div>
              <div
                className={styles.feedbackTilesContainer}
                ref={tilesContainerRef}
              >
                <FeedbackTile
                  rating={5}
                  date="2024-06-01"
                  message="I had dinner here last night with my family, and overall it was a wonderful experience. The food was fresh, flavorful, and beautifully presented, especially the grilled salmon, which was cooked perfectly. Our server was very polite and attentive, checking on us just enough without being intrusive. The only minor downside was that our appetizers took a little longer than expected to arrive, but the staff apologized and handled it professionally. The ambiance was cozy and welcoming, with just the right amount of background music and warm lighting. We’ll definitely be coming back soon and recommending this place to friends."
                  author="John Doe"
                />
                <FeedbackTile
                  rating={4}
                  date="2024-06-01"
                  message="I had dinner here last night with my family, and overall it was a wonderful experience. The food was fresh, flavorful, and beautifully presented, especially the grilled salmon, which was cooked perfectly. Our server was very polite and attentive, checking on us just enough without being intrusive. The only minor downside was that our appetizers took a little longer than expected to arrive, but the staff apologized and handled it professionally. The ambiance was cozy and welcoming, with just the right amount of background music and warm lighting. We’ll definitely be coming back soon and recommending this place to friends."
                  author="John Doe"
                />
                <FeedbackTile
                  rating={2}
                  date="2024-06-01"
                  message="I had dinner here last night with my family, and overall it was a wonderful experience. The food was fresh, flavorful, and beautifully presented, especially the grilled salmon, which was cooked perfectly. Our server was very polite and attentive, checking on us just enough without being intrusive. The only minor downside was that our appetizers took a little longer than expected to arrive, but the staff apologized and handled it professionally. The ambiance was cozy and welcoming, with just the right amount of background music and warm lighting. We’ll definitely be coming back soon and recommending this place to friends."
                  author="John Doe"
                />
                <FeedbackTile
                  rating={5}
                  date="2024-06-01"
                  message="I had dinner here last night with my family, and overall it was a wonderful experience. The food was fresh, flavorful, and beautifully presented, especially the grilled salmon, which was cooked perfectly. Our server was very polite and attentive, checking on us just enough without being intrusive. The only minor downside was that our appetizers took a little longer than expected to arrive, but the staff apologized and handled it professionally. The ambiance was cozy and welcoming, with just the right amount of background music and warm lighting. We’ll definitely be coming back soon and recommending this place to friends."
                  author="John Doe"
                />
                <FeedbackTile
                  rating={3}
                  date="2024-06-01"
                  message="I had dinner here last night with my family, and overall it was a wonderful experience. The food was fresh, flavorful, and beautifully presented, especially the grilled salmon, which was cooked perfectly. Our server was very polite and attentive, checking on us just enough without being intrusive. The only minor downside was that our appetizers took a little longer than expected to arrive, but the staff apologized and handled it professionally. The ambiance was cozy and welcoming, with just the right amount of background music and warm lighting. We’ll definitely be coming back soon and recommending this place to friends."
                  author="John Doe"
                />
              </div>
            </div>
          </div>
        </Tile>
      </div>
    </Grid>
  )
}

export default FeedbackPage
